import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUserPlus, FaTrash, FaKey, FaCopy, FaCheck, FaShieldAlt } from 'react-icons/fa';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/GlobalAPI';
import AdminPageHeader from './AdminPageHeader';

const AVAILABLE_MODULES = [
  'Inquiry',
  'Influencer Request',
  'Manage Influencers',
  'HireMeRequest',
  'Homepage',
  'Services',
  'Blog',
  'About Us',
  'UpSkill',
  'Premium Influencers',
  'Users',
  'Settings'
];

const AdminAdmins = () => {
  const [subadmins, setSubadmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedModules, setSelectedModules] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchSubadmins();
  }, []);

  const fetchSubadmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.ADMIN_SUBADMINS);
      setSubadmins(response.data);
    } catch (err) {
      console.error('Error fetching subadmins:', err);
      setError('Failed to load sub-admins list');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789#@$%&*';
    let newPass = '';
    for (let i = 0; i < 10; i++) {
      newPass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(newPass);
  };

  const handleModuleToggle = (moduleName) => {
    setSelectedModules(prev =>
      prev.includes(moduleName)
        ? prev.filter(m => m !== moduleName)
        : [...prev, moduleName]
    );
  };

  const handleSelectAllModules = () => {
    if (selectedModules.length === AVAILABLE_MODULES.length) {
      setSelectedModules([]);
    } else {
      setSelectedModules([...AVAILABLE_MODULES]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setActionLoading(true);
      await axios.post(API_ENDPOINTS.ADMIN_CREATE_SUBADMIN, {
        email,
        password,
        allowedModules: selectedModules
      });

      setSuccess('Sub-admin account created successfully!');
      setEmail('');
      setPassword('');
      setSelectedModules([]);
      fetchSubadmins();
    } catch (err) {
      console.error('Error creating subadmin:', err);
      setError(err.response?.data?.error || 'Failed to create sub-admin');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id, subadminEmail) => {
    if (!window.confirm(`Are you sure you want to delete sub-admin access for ${subadminEmail}?`)) {
      return;
    }

    try {
      setActionLoading(true);
      await axios.delete(`${API_ENDPOINTS.ADMIN_DELETE_SUBADMIN}/${id}`);
      setSuccess('Sub-admin access revoked successfully!');
      fetchSubadmins();
    } catch (err) {
      console.error('Error deleting subadmin:', err);
      setError('Failed to delete sub-admin');
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="pt-32 p-6 max-w-7xl mx-auto space-y-8">
      <AdminPageHeader 
        title="Admin User Access Control (RBAC)" 
        subtitle="Manage sub-admins, generate passwords, and specify allowed panel modules."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Create Sub-admin Card */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:col-span-1 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6"
        >
          <div className="flex items-center space-x-3 text-gray-800 pb-2 border-b border-gray-100">
            <FaUserPlus className="w-5 h-5 text-orange-500" />
            <h2 className="font-bold text-lg">Create Sub-Admin</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-500 outline-none transition"
                placeholder="subadmin@cyborgsapient.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  required
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-500 outline-none transition font-mono"
                  placeholder="Click Generate"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  className="px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg transition flex items-center justify-center"
                  title="Generate Password"
                >
                  <FaKey className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Modules Checkboxes */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">Permitted Modules</label>
                <button
                  type="button"
                  onClick={handleSelectAllModules}
                  className="text-xs text-orange-600 hover:text-orange-700 font-semibold"
                >
                  {selectedModules.length === AVAILABLE_MODULES.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-gray-50">
                {AVAILABLE_MODULES.map(moduleName => {
                  const isChecked = selectedModules.includes(moduleName);
                  return (
                    <label key={moduleName} className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleModuleToggle(moduleName)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span>{moduleName}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {error && <div className="text-red-500 text-sm">{error}</div>}
            {success && <div className="text-green-600 text-sm">{success}</div>}

            <button
              type="submit"
              disabled={actionLoading}
              className={`w-full py-2.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg font-bold shadow-md hover:from-orange-600 hover:to-pink-600 transition ${actionLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {actionLoading ? 'Creating...' : 'Create Sub-Admin'}
            </button>
          </form>
        </motion.div>

        {/* Sub-admins List Card */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6"
        >
          <div className="flex items-center space-x-3 text-gray-800 pb-2 border-b border-gray-100">
            <FaShieldAlt className="w-5 h-5 text-pink-500" />
            <h2 className="font-bold text-lg">Active Sub-Admins</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : subadmins.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No sub-admin accounts created yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Password</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Allowed Modules</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {subadmins.map((sub) => (
                    <tr key={sub._id} className="hover:bg-gray-50/55 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-800">{sub.email}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                        <div className="flex items-center space-x-2">
                          <span>{sub.password}</span>
                          <button
                            onClick={() => copyToClipboard(sub.password, sub._id)}
                            className="text-gray-400 hover:text-gray-600 transition"
                            title="Copy Password"
                          >
                            {copiedId === sub._id ? <FaCheck className="w-3.5 h-3.5 text-green-500" /> : <FaCopy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        <div className="flex flex-wrap gap-1">
                          {sub.allowedModules && sub.allowedModules.length > 0 ? (
                            sub.allowedModules.map((m) => (
                              <span key={m} className="px-2 py-0.5 text-xs font-medium bg-orange-50 text-orange-600 rounded-full border border-orange-100">
                                {m}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400 italic">None</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDelete(sub._id, sub.email)}
                          disabled={actionLoading}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                          title="Revoke Access"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAdmins;
